export const retailerRoles = [
  'retailer-admin',
  'retailer-admin-ro',
  'retailer-manager',
  'retailer-integration',
] as const;

export const advertiserRoles = ['advertiser', 'advertiser-ro'] as const;

const roles = [...retailerRoles, ...advertiserRoles] as const;

export type Role = (typeof roles)[number];

export type RetailerRole = (typeof retailerRoles)[number];

export type AdvertiserRole = (typeof advertiserRoles)[number];

const scopes: Record<Role, string[]> = {
  'retailer-admin': [
    'read:placement',
    'read-all:placement',
    'write-all:placement',
    'read:campaign',
    'read-all:campaign',
    'write:campaign',
    'write-all:campaign',
    'read:audience',
    'read-all:audience',
    'write:audience',
    'write-all:audience',
    'read-all:catalog',
    'write-all:catalog',
    'read-all:product',
    'write-all:product',
    'read-all:store',
    'write-all:store',
    'read-all:import',
    'write-all:import',
    'write-all:ad-set',
    'read:event',
    'read-all:event',
    'write-all:event',
    'read:advertiser',
    'read-all:advertiser',
    'write:advertiser',
    'write-all:advertiser',
    'read:user',
    'read-all:user',
    'write:user',
    'write-all:user',
    'write:own-user',
    'read:project',
    'write:project',
    'read:profile',
    'read-all:change',
    'read:change',
    'read:tenancy',
    'read-all:tenancy',
    'write-all:tenancy',
    'read-all:extension',
    'write-all:extension',
    'read:forecast',
    'read-all:forecast',
    'read-all:invoice',
    'read:reporting-job',
    'read-all:reporting-job',
    'write:reporting-job',
    'write-all:reporting-job',
    'read:subscription',
    'read-all:subscription',
    'write:subscription',
    'write-all:subscription',
    'read-all:custom-object',
    'read:custom-object',
    'write-all:custom-object',
    'write:custom-object',
    'read-all:application',
    'write-all:application',
  ],
  'retailer-admin-ro': [
    'read:placement',
    'read-all:placement',
    'read:campaign',
    'read-all:campaign',
    'read:audience',
    'read-all:audience',
    'read-all:catalog',
    'read-all:product',
    'read-all:store',
    'read-all:import',
    'read:event',
    'read-all:event',
    'read:forecast',
    'read-all:forecast',
    'read:advertiser',
    'read-all:advertiser',
    'read:user',
    'read-all:user',
    'read:project',
    'read:profile',
    'read-all:change',
    'read:change',
    'read:tenancy',
    'read-all:tenancy',
    'read-all:extension',
    'read-all:invoice',
    'read:reporting-job',
    'read-all:reporting-job',
    'read-all:custom-object',
    'read:custom-object',
    'read-all:application',
  ],
  'retailer-manager': [
    'read:placement',
    'read-all:placement',
    'read:campaign',
    'read-all:campaign',
    'write:campaign',
    'write-all:campaign',
    'read:audience',
    'read-all:audience',
    'write:audience',
    'write-all:audience',
    'read-all:catalog',
    'read-all:product',
    'read-all:store',
    'read:advertiser',
    'read-all:advertiser',
    'write:advertiser',
    'write-all:advertiser',
    'read:user',
    'read-all:user',
    'write:user',
    'read:event',
    'read-all:event',
    'read:profile',
    'read-all:change',
    'read:change',
    'read:tenancy',
    'read-all:tenancy',
    'read:forecast',
    'read-all:forecast',
    'read:reporting-job',
    'read-all:reporting-job',
    'write:reporting-job',
    'write-all:reporting-job',
    'read:subscription',
    'read-all:subscription',
    'write:subscription',
    'write-all:subscription',
    'read-all:custom-object',
    'read:custom-object',
    'write-all:custom-object',
    'write:custom-object',
  ],
  'retailer-integration': [
    'read:placement',
    'read-all:placement',
    'read:campaign',
    'read-all:campaign',
    'read:audience',
    'read-all:audience',
    'read-all:catalog',
    'write-all:catalog',
    'read-all:product',
    'write-all:product',
    'read-all:store',
    'write-all:store',
    'read-all:import',
    'write-all:import',
    'write-all:ad-set',
    'read:event',
    'read-all:event',
    'write-all:event',
    'read:profile',
    'read-all:change',
    'read:change',
    'read:tenancy',
    'read-all:tenancy',
    'read:forecast',
    'read-all:forecast',
    'read:reporting-job',
    'read-all:reporting-job',
  ],
  advertiser: [
    'read:user',
    'read-all:catalog',
    'read-all:product',
    'read:store',
    'read:placement',
    'read:campaign',
    'write:campaign',
    'read:audience',
    'write:audience',
    'read:advertiser',
    'write:advertiser',
    'write:user',
    'read:event',
    'read:profile',
    'read:change',
    'read:tenancy',
    'read:forecast',
    'read:reporting-job',
    'write:reporting-job',
    'read:subscription',
    'write:subscription',
    'read:custom-object',
    'write:custom-object',
  ],
  'advertiser-ro': [
    'read:user',
    'read-all:catalog',
    'read-all:product',
    'read:store',
    'read:placement',
    'read:campaign',
    'read:audience',
    'read:advertiser',
    'read:event',
    'read:forecast',
    'read:profile',
    'read:change',
    'read:tenancy',
    'read:reporting-job',
    'read:custom-object',
  ],
};

export interface Account {
  projectId: string;
  advertiserId: string;
}

export interface RawUserApiResponse {
  id?: string;
  name?: string;
  email?: string;
  role?: Role;
  advertisers?: UserAdvertiser[];
  status?: UserStatus;
  last_login?: string;
  invitation_expires_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface UserAdvertiser {
  advertiser_id?: string;
  company_name?: string;
}

export enum UserStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
  ARCHIVED = 'ARCHIVED',
}

export interface RawUserProfileApiResponse {
  id: string;
  project_id: string;
  advertiser_id: string;
  email: string;
  name: string;
  role: Role;
  last_login: string;
  created_at: string;
  projects?: UserProjectResponse[];
}

export interface UserProjectResponse {
  project_key?: string;
}

export interface RawUserProjectsApiResponse {
  project_key: string;
}

export interface UserRequest<Role> {
  email: string;
  role?: Role;
  advertiser_id?: string | null;
}

const retailerAdminExtraScopes = roles.map((role) => getRoleScopeName('write-all', role));

// Deliberate subset: retailer-manager cannot act on admin-level roles.
const retailerManagerExtraScopes = [
  getRoleScopeName('write-all', 'retailer-integration'),
  getRoleScopeName('write-all', 'advertiser'),
  getRoleScopeName('write-all', 'advertiser-ro'),
];

const retailerIntegrationExtraScopes: string[] = [];

const advertiserExtraScopes = advertiserRoles.map((role) => getRoleScopeName('write-all', role));

function getExtraScopes(role: Role | ''): string[] {
  if (role === 'retailer-admin') {
    return retailerAdminExtraScopes;
  }

  if (role === 'retailer-manager') {
    return retailerManagerExtraScopes;
  }

  if (role === 'retailer-integration') {
    return retailerIntegrationExtraScopes;
  }

  if (role === 'advertiser') {
    return advertiserExtraScopes;
  }

  return [];
}

export function getRoleScopeName(action: string, role: Role | ''): string {
  return `${action}:role-${role}`;
}

export function hasScopeToActOnRole(actor: Role | '', subject: Role | '', action: string) {
  if (actor === '') {
    return false;
  }

  if (subject === '') {
    return true;
  }

  const actorScopes = getExtraScopes(actor);

  return actorScopes.includes(getRoleScopeName(action, subject));
}

export function getRoleScopes(role: Role | undefined) {
  if (!role) {
    return [];
  }

  return scopes[role as RetailerRole];
}

export function getAllScopes(): string[] {
  const allScopes = new Set<string>();
  for (const roleScopes of Object.values(scopes)) {
    for (const scope of roleScopes) {
      allScopes.add(scope);
    }
  }
  return Array.from(allScopes);
}

export function getViewScopeScopes(viewScope: 'advertiser' | 'retailer') {
  if (viewScope === 'advertiser') {
    return getRoleScopes('advertiser');
  } else if (viewScope === 'retailer') {
    return getRoleScopes('retailer-admin');
  }

  throw new Error(`Unknown view scope: ${viewScope}`);
}

export function hasScope(role: Role | undefined, scope: string) {
  return hasAllScopes(role, [scope]);
}

export function hasSomeScopes(role: Role | undefined, scopes: string[]) {
  if (!role) {
    return false;
  }

  const roleScopes = getRoleScopes(role);

  if (!roleScopes) {
    return false;
  }

  return scopes.some((scope) => roleScopes.includes(scope));
}

export function hasAllScopes(role: Role | undefined, scopes: string[]) {
  if (!role) {
    return false;
  }

  const roleScopes = getRoleScopes(role);

  if (!roleScopes) {
    return false;
  }

  return scopes.every((scope) => roleScopes.includes(scope));
}

export function isMe(profile: RawUserProfileApiResponse, id: string) {
  return profile.id === id;
}

export function isRetailerRole(role: Role | undefined) {
  if (!role) {
    return false;
  }

  return retailerRoles.includes(role as RetailerRole);
}

export function isAdvertiserRole(role: Role | undefined) {
  if (!role) {
    return false;
  }

  return advertiserRoles.includes(role as AdvertiserRole);
}
